import { useCallback, useEffect, Fragment, memo, FC, useMemo, useState } from 'react'
import { Button, Box } from '@chakra-ui/react'
import { useRouter } from 'next/router'
import { Link } from '@chakra-ui/next-js'

import { CustomFeedback } from 'components'
import { useUserPermissionsManager } from 'contexts/UserPermissionsManager/Provider'
import { getModuleByPathOnURL } from 'utils/module'
import { APP_CITIZEN_URL } from 'constants/index'

type Props = {
  children: JSX.Element
  moduleWithPageWithoutPermission?: boolean
}

const HasPermissionsToAccessThisModule: FC<Props> = ({ children, moduleWithPageWithoutPermission = false }) => {
  const { asPath } = useRouter()
  const router = useRouter()
  const [withoutPermission, setWithoutPermission] = useState(false)
  const [onlyAccessPermission, setOnlyAccessPermission] = useState(false)

  const MODULE = useMemo(() => {
    const moduleOnUrl = asPath.split('/')[1]
    return getModuleByPathOnURL(moduleOnUrl)
  }, [asPath])

  const { userPermissions } = useUserPermissionsManager()

  const checkUserHasPermissions = useCallback(() => {
    if (!userPermissions) return false

    const isAdmin = userPermissions.find((element) => element === `IMPACTO-${MODULE.API_NAME}-ADMIN`)

    if (isAdmin) {
      return
    } else {
      const modulePermissions = userPermissions.filter((element) => element.includes(`IMPACTO-${MODULE.API_NAME}-`))

      if (modulePermissions.length === 0) {
        setWithoutPermission(true)
        return
      }

      const isAgentPublic = userPermissions.find((element) => element === `IMPACTO-${MODULE.API_NAME}-ACCESS`)

      if (!isAgentPublic) {
        setWithoutPermission(true)
        return
      }

      if (!moduleWithPageWithoutPermission) {
        const modulePermissionsWithoutAccess = modulePermissions.filter(
          (element) => element !== `IMPACTO-${MODULE.API_NAME}-ACCESS`
        )

        if (isAgentPublic && modulePermissionsWithoutAccess.length === 0) {
          setOnlyAccessPermission(true)
          return
        }
      }
    }

    return
  }, [])

  useEffect(() => {
    async function checkPermission() {
      checkUserHasPermissions()
    }

    checkPermission()
  }, [])

  if (withoutPermission) {
    return (
      <CustomFeedback height="100vh" context="withoutPermissionInTheModule">
        <Box>
          <Link href={APP_CITIZEN_URL}>
            <Button w="full" size="lg" mb={4}>
              Ir para o APP Cidadão
            </Button>
          </Link>
          <Button w="full" size="lg" variant="ghost" onClick={() => router.back()}>
            Voltar
          </Button>
        </Box>
      </CustomFeedback>
    )
  }

  if (onlyAccessPermission) {
    return (
      <CustomFeedback height="100vh" context="onlyModulePermissions">
        <Box>
          <Link href={APP_CITIZEN_URL}>
            <Button w="full" size="lg" mb={4}>
              Ir para o APP Cidadão
            </Button>
          </Link>
          <Button w="full" size="lg" variant="ghost" onClick={() => router.back()}>
            Voltar
          </Button>
        </Box>
      </CustomFeedback>
    )
  }

  return <Fragment>{children}</Fragment>
}

export default memo(HasPermissionsToAccessThisModule)
